import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { type Question } from '../../content';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { AssessmentHeader } from './AssessmentHeader';
import { AssessmentQuestionCard } from './AssessmentQuestionCard';
import { AssessmentMCOptions } from './AssessmentMCOptions';
import { AssessmentIntroScreen } from './AssessmentIntroScreen';
import { AssessmentCompleteScreen } from './AssessmentCompleteScreen';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { useFocusTrap } from '../../app/hooks/useFocusTrap';
import { error as logError } from '../../utils/logger';
import type { AssessmentDomainId } from '../types';
interface AssessmentSessionProps {
  questions: Question[];
  domainId?: AssessmentDomainId;
  onComplete: () => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number) => void;
  /**
   * R2-L1 (Assessment audit 2026): Fired whenever the visible question
   * changes WITHOUT an answer being recorded (e.g. Previous navigation).
   * The parent hook uses this to reset its per-question timer so
   * `timeSpent` is not inflated by review time.
   */
  onQuestionChange?: () => void;
  /**
   * R2-RESUME (F-AS-R1-RESUME-LOSS, May 2026): When the user resumes a
   * recovered assessment, the parent provides:
   *  - `startIndex`: which question to land on (first unanswered),
   *  - `initialChoices`: previously selected option per question index,
   *  - `initialAnsweredIndices`: which indices already have an answer
   *    recorded server-side (used to render the progress dots),
   *  - `skipIntro`: skip the intro screen and go straight to questions.
   */
  startIndex?: number;
  initialChoices?: Record<number, number>;
  initialAnsweredIndices?: number[];
  skipIntro?: boolean;
}
type SessionPhase = 'intro' | 'questions' | 'complete';
export function AssessmentSession({
  questions,
  domainId = 'rekenen',
  // fallback
  onComplete,
  onExit,
  onAnswer,
  onQuestionChange,
  startIndex = 0,
  initialChoices,
  initialAnsweredIndices,
  skipIntro = false
}: AssessmentSessionProps) {
  const [phase, setPhase] = useState<SessionPhase>(skipIntro ? 'questions' : 'intro');
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>(() => initialChoices ? {
    ...initialChoices
  } : {});
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<number>>(() => new Set(initialAnsweredIndices ?? []));
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const exitModalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(showExitConfirm, exitModalRef);
  const [elapsedMs, setElapsedMs] = useState(0);
  const startTimeRef = useRef(Date.now());
  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;
  const selectedOriginalIndex = selectedOptions[currentIndex] ?? null;
  // Global Timer — starts when entering questions phase
  useEffect(() => {
    if (phase !== 'questions') return;
    startTimeRef.current = Date.now();
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - startTimeRef.current);
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);
  // Escape key for exit
  useEscapeKey(() => {
    if (phase === 'questions') setShowExitConfirm(true);
  });
  // Navigation
  const handleNext = useCallback(() => {
    if (selectedOriginalIndex === null) return;
    // Record answer
    const isCorrect = selectedOriginalIndex === currentQuestion.correctAnswer;
    onAnswer(currentQuestion.id, isCorrect, selectedOriginalIndex);
    setAnsweredQuestions(prev => new Set(prev).add(currentIndex));
    if (isLastQuestion) {
      // Capture exact elapsed time before transitioning — the interval
      // only ticks every 1 s, so elapsedMs could be up to ~1 s behind.
      setElapsedMs(Date.now() - startTimeRef.current);
      setPhase('complete');
    } else {
      setCurrentIndex(prev => prev + 1);
    }
  }, [selectedOriginalIndex, currentQuestion, currentIndex, isLastQuestion, onAnswer]);
  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      onQuestionChange?.();
    }
  }, [currentIndex, onQuestionChange]);
  const handleSelect = useCallback((originalIndex: number) => {
    setSelectedOptions(prev => ({
      ...prev,
      [currentIndex]: originalIndex
    }));
  }, [currentIndex]);
  // Enter key to submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase === 'questions' && e.key === 'Enter' && selectedOriginalIndex !== null && !showExitConfirm) {
        handleNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, selectedOriginalIndex, showExitConfirm, handleNext]);
  if (!currentQuestion && phase === 'questions') {
    logError('AssessmentSession: No current question found');
    return null;
  }
  if (phase === 'intro') {
    return <AssessmentIntroScreen domainId={domainId} questionCount={questions.length} onStart={() => {
      setPhase('questions');
      startTimeRef.current = Date.now();
    }} onCancel={onExit} data-id="element-1042" data-mp="2c2a3rofcs0j2" />;
  }
  if (phase === 'complete') {
    return <AssessmentCompleteScreen questionCount={questions.length} elapsedMs={elapsedMs} domainId={domainId} onViewReport={onComplete} data-id="element-1043" data-mp="2qsr3n3ccaidu" />;
  }
  return <div className="min-h-screen bg-gradient-to-b from-indigo-50/80 to-slate-50 flex flex-col font-sans h-screen overflow-hidden" data-id="element-1044" data-mp="2uu2ufc6y99nm">
      <AssessmentHeader currentQuestion={currentIndex + 1} totalQuestions={questions.length} elapsedMs={elapsedMs} onExit={() => setShowExitConfirm(true)} onPrevious={handlePrevious} canGoPrevious={currentIndex > 0} answeredIndices={answeredQuestions} data-id="element-1045" data-mp="34t1tq45trsxy" />
      

      <div className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 mt-16 flex flex-col min-h-0 overflow-y-auto pb-24" data-id="element-1046" data-mp="ztak5gqrinjo">
        <div className="flex-1 flex flex-col relative justify-center" data-id="element-1047" data-mp="1eif3505dsvsu">
          <AnimatePresence mode="wait" data-id="element-1048" data-mp="19oaw6gw48nrr">
            <motion.div key={currentQuestion.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} className="w-full" data-id="element-1049" data-mp="1nha7e6ov7vti">
              
              <AssessmentQuestionCard question={currentQuestion} data-id="element-1050" data-mp="1shere6ozf8oh">
                <AssessmentMCOptions questionId={currentQuestion.id} options={currentQuestion.options || []} onSelect={handleSelect} selectedOriginalIndex={selectedOriginalIndex} data-id="element-1051" data-mp="31f83wrng2v4" />
                
              </AssessmentQuestionCard>

              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="mt-8 flex justify-center" data-id="element-1052" data-mp="29qqs7voyod9r">
                
                <Button size="xl" variant="primary" disabled={selectedOriginalIndex === null} onClick={handleNext} className="w-full sm:w-auto sm:min-w-[320px] shadow-lg rounded-2xl text-fluid-lg font-bold" data-id="element-1053" data-mp="24yh2ogrrdf80">
                  
                  <span className="mr-2" data-id="element-1054" data-mp="1vz2fbq6w1krq">
                    {isLastQuestion ? 'Toets Afronden' : 'Volgende'}
                  </span>
                  <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-1055" data-mp="1h3ef0c4oxht4" />
                </Button>
              </motion.div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Assessment Exit Modal */}
        <AnimatePresence data-id="element-1056" data-mp="3gdw01lnvq93n">
          {showExitConfirm && <>
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50" onClick={() => setShowExitConfirm(false)} data-id="element-1057" data-mp="3gc1r75pvu7fx" />
            
              <motion.div ref={exitModalRef} role="dialog" aria-modal="true" aria-labelledby="assessment-exit-title" aria-describedby="assessment-exit-desc" initial={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} exit={{
            opacity: 0,
            scale: 0.9,
            y: 20
          }} transition={{
            type: 'spring',
            bounce: 0.2,
            duration: 0.4
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1058" data-mp="1udepe67uqq87">
              
                <Card className="max-w-md w-full p-8 shadow-2xl border border-slate-200 bg-white rounded-3xl" data-id="element-1059" data-mp="35td0sgqi5ocl">
                  <div className="text-center" data-id="element-1060" data-mp="30t8luv58t1oh">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1061" data-mp="38dj7gw6i3swu">
                      <AlertCircle className="w-7 h-7 text-amber-600" data-id="element-1062" data-mp="3dc2vr38khgsd" />
                    </div>
                    <h2 id="assessment-exit-title" className="text-2xl font-black text-slate-900 mb-3 tracking-tight" data-id="element-1063" data-mp="3i4vzpow7ol3e">
                    
                      Toets stoppen?
                    </h2>
                    <p id="assessment-exit-desc" className="text-slate-600 font-medium mb-6 leading-relaxed" data-id="element-1064" data-mp="2ph9lznt9s02t">
                    
                      Je voortgang wordt opgeslagen als onvolledig. Je kunt
                      later opnieuw beginnen.
                    </p>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-6 text-left" data-id="element-1065" data-mp="34e0kzvz179rw">
                      <p className="text-sm text-slate-700 font-medium" data-id="element-1066" data-mp="emlymdabdsfv">
                        Je hebt {answeredQuestions.size} van de{' '}
                        {questions.length} vragen beantwoord.
                      </p>
                    </div>
                    <div className="flex gap-3" data-id="element-1067" data-mp="1qlxht6c4ii1k">
                      <Button onClick={() => setShowExitConfirm(false)} variant="primary" size="lg" fullWidth data-id="element-1068" data-mp="1qkme16iohaag">
                      
                        Doorgaan
                      </Button>
                      <Button onClick={() => {
                    setShowExitConfirm(false);
                    onExit();
                  }} variant="secondary" size="lg" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" data-id="element-1069" data-mp="20afh1j4r8h33">
                      
                        Stoppen
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </>}
        </AnimatePresence>
      </div>
    </div>;
}